import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, MapPin, ArrowDown } from "lucide-react";

const FALLBACK = [
  {
    slug: "the-azure-residences",
    name: "The Azure Residences",
    location: "Patia, Bhubaneswar",
    typology: "3 & 4 BHK Sky Residences",
    status: "ongoing",
    year: 2026,
    progress: 62,
    hero_image: "https://images.unsplash.com/photo-1614595737476-42487331b8a1?auto=format&fit=crop&w=2400&q=90",
    description: "A tranquil residential landmark rising above the city — Azure blends architectural clarity with layered greenery, oversized balconies, and sunlit interiors designed for a slower, more intentional life.",
    rera: "RP/19/2025/AZR-1401",
  },
  {
    slug: "kliving-plaza",
    name: "KLiving Plaza",
    location: "Kalinga Nagar, Bhubaneswar",
    typology: "Grade-A Commercial Tower",
    status: "ongoing",
    year: 2027,
    progress: 41,
    hero_image: "https://images.unsplash.com/photo-1619218070141-bcfeb8b93074?auto=format&fit=crop&w=2400&q=90",
    description: "A commercial address that refuses to be ordinary — pre-certified green architecture, double-height retail lobbies and a public forecourt designed for the city, not just the tenants.",
    rera: "RP/19/2025/PLZ-2207",
  },
  {
    slug: "the-meridian-exchange",
    name: "The Meridian Exchange",
    location: "Chandrasekharpur, Bhubaneswar",
    typology: "Mixed-use Landmark",
    status: "upcoming",
    year: 2028,
    progress: 12,
    hero_image: "https://images.unsplash.com/photo-1558661091-5cc1b64d0dc5?auto=format&fit=crop&w=2400&q=90",
    description: "A civic-scale mixed-use development anchored by a public plaza — a place where residences, workplaces and cultural venues share the same address and the same light.",
    rera: "RP/19/2025/MRD-3110",
  },
  {
    slug: "the-cove-villas",
    name: "The Cove Villas",
    location: "Jatni, Bhubaneswar",
    typology: "Boutique Villas · Completed 2024",
    status: "completed",
    year: 2024,
    progress: 100,
    hero_image: "https://images.unsplash.com/photo-1613545325268-9265e1609167?auto=format&fit=crop&w=2400&q=90",
    description: "Twelve boutique villas around a central lily-pond courtyard — completed 2024, now home to a small community of collectors, doctors and creators.",
    rera: "RP/19/2022/COV-0912",
  },
];

function ProjectSection({ project, index, total }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.0]);
  const contentY = useTransform(scrollYProgress, [0, 0.5, 1], ["30%", "0%", "-20%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.25, 0.75, 1], [0, 1, 1, 0]);
  const numberX = useTransform(scrollYProgress, [0, 1], ["-10%", "5%"]);

  const idx = String(index + 1).padStart(2, "0");
  const totalStr = String(total).padStart(2, "0");

  return (
    <section
      ref={ref}
      data-testid={`project-v2-${project.slug}`}
      className="relative min-h-[100svh] w-full overflow-hidden"
    >
      {/* Cinematic parallax image */}
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 will-change-transform">
        <img
          src={project.hero_image}
          alt={project.name}
          className="w-full h-full object-cover"
        />
      </motion.div>

      {/* Cinematic overlays — heavy charcoal to keep text sharp */}
      <div className="absolute inset-0 bg-charcoal/55" />
      <div className="absolute inset-0 v2-cine-vignette" />
      <div className="absolute inset-0 v2-cine-fade-left" />
      <div className="absolute inset-0 v2-cine-fade-bottom" />

      {/* Gigantic index number as watermark */}
      <motion.div
        style={{ x: numberX }}
        className="absolute -left-6 md:left-4 top-1/2 -translate-y-1/2 pointer-events-none select-none"
      >
        <span className="font-serif-display italic text-charcoal_ink/[0.06] text-[280px] md:text-[440px] lg:text-[560px] leading-none">
          {idx}
        </span>
      </motion.div>

      {/* Content grid */}
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 min-h-[100svh] flex items-center px-6 md:px-10"
      >
        <div className="max-w-[1400px] mx-auto w-full grid grid-cols-12 gap-6 md:gap-12 items-end">
          {/* Left column — index, typology, name */}
          <div className="col-span-12 md:col-span-7">
            <div className="flex items-center gap-4 chapter-num text-charcoal_ink/70 mb-8">
              <span className="glow-lime tracking-[0.32em]">CHAPTER {idx}</span>
              <span className="w-16 h-px bg-glow_lime glow-box" />
              <span className="text-charcoal_ink/50">{project.status?.toUpperCase()}</span>
            </div>

            <div className="chapter-num text-charcoal_ink/60 mb-4">
              {project.typology?.toUpperCase()}
            </div>

            <h3 className="font-serif-display leading-[0.94] tracking-tight text-charcoal_ink text-6xl md:text-7xl lg:text-[112px]">
              {project.name}
              <span className="glow-lime">.</span>
            </h3>

            <p className="mt-8 text-charcoal_ink/80 text-lg leading-relaxed max-w-xl">
              {project.description}
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#contact-v2"
                className="btn-kl-v2 btn-kl-v2-solid"
              >
                Enquire About {project.name?.split(" ").slice(-1)[0] || "Project"} <ArrowUpRight className="w-4 h-4" />
              </a>
              <a href="#progress-v2" className="btn-kl-v2">
                Live Progress <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Right column — meta + progress */}
          <div className="col-span-12 md:col-span-5 md:pl-8 md:border-l md:border-charcoal_ink/15 space-y-8">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <div className="chapter-num text-charcoal_ink/50 mb-2">LOCATION</div>
                <div className="flex items-start gap-2 text-charcoal_ink">
                  <MapPin className="w-4 h-4 mt-1 text-glow_lime glow-icon shrink-0" />
                  <span className="text-sm md:text-base">{project.location}</span>
                </div>
              </div>
              <div>
                <div className="chapter-num text-charcoal_ink/50 mb-2">HANDOVER</div>
                <div className="text-charcoal_ink text-sm md:text-base">{project.year}</div>
              </div>
              <div className="col-span-2">
                <div className="chapter-num text-charcoal_ink/50 mb-2">ORERA REGISTERED NO.</div>
                <div className="font-mono-kl text-charcoal_ink text-sm">{project.rera}</div>
              </div>
            </div>

            {/* Glowing progress bar */}
            <div>
              <div className="flex items-baseline justify-between mb-3">
                <span className="chapter-num text-charcoal_ink/50">CONSTRUCTION PROGRESS</span>
                <span className="font-serif-display text-4xl text-charcoal_ink tabular-nums">
                  {String(project.progress).padStart(2, "0")}<span className="glow-lime">%</span>
                </span>
              </div>
              <div className="v2-progress-track">
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: `${project.progress}%` }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
                  className="v2-progress-fill"
                />
              </div>
            </div>

            <div className="chapter-num text-charcoal_ink/40 tracking-[0.3em]">
              {idx} / {totalStr} · KLIVING PORTFOLIO
            </div>
          </div>
        </div>
      </motion.div>

      {/* Scroll cue only on non-last */}
      {index < total - 1 && (
        <motion.div
          initial={{ opacity: 0, y: -8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.6 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-charcoal_ink/60"
        >
          <span className="chapter-num tracking-[0.32em]">CONTINUE</span>
          <ArrowDown className="w-4 h-4 animate-bounce text-glow_lime glow-icon" />
        </motion.div>
      )}
    </section>
  );
}

export default function ProjectsCinematic({ projects }) {
  // Fix: Explicitly check if it's a valid array to prevent runtime mapping crashes
  const source = Array.isArray(projects) && projects.length ? projects : FALLBACK;
  
  const items = source.slice(0, 4).map((p, i) => ({
    ...p,
    description: p.description || "",
    progress: p.progress ?? FALLBACK[i]?.progress ?? 0,
    hero_image: p.hero_image || FALLBACK[i]?.hero_image,
    rera: p.rera || `RP/19/${p.year || 2025}/KL-${(p.slug || "").slice(0, 3).toUpperCase()}`,
  }));

  return (
    <div id="projects-v2" data-testid="projects-v2" className="bg-charcoal">
      {/* Section intro — sticky-ish title */}
      <section className="relative bg-charcoal text-charcoal_ink px-6 md:px-10 py-24 md:py-32 v2-gridlines">
        <div className="max-w-[1400px] mx-auto">
          <div className="chapter-num mb-6 flex items-center">
            <span className="inline-block w-8 h-px bg-glow_lime glow-box mr-3" />
            <span className="glow-lime-soft text-glow_lime">PORTFOLIO · SELECTED WORKS</span>
          </div>
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
            <div className="col-span-12 md:col-span-8">
              <motion.h2
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="font-serif-display leading-[0.95] tracking-tight text-charcoal_ink text-5xl md:text-6xl lg:text-[88px]"
              >
                Featured <span className="italic glow-lime">Projects</span>.
              </motion.h2>
            </div>
            <div className="col-span-12 md:col-span-4 md:text-right">
              <p className="text-charcoal_ink/70 max-w-sm md:ml-auto">
                Scroll through each landmark — a full-screen chapter for every address. {items.length} projects, one continuous story.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Full-screen cinematic project sections */}
      {items.map((p, i) => (
        <ProjectSection key={p.slug || i} project={p} index={i} total={items.length} />
      ))}
    </div>
  );
}